import { Component, ElementRef, inject, input, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, viewChild } from '@angular/core';
import { type FormGroup } from '@angular/forms';
import { heightAnimation } from '@modules/common/animations/height.animation';
import { Section, type SectionFormGroup } from '@modules/common/types/section';
import { DragData } from '@modules/drag-n-drop/types/drag-data';
import { BoardService } from '@modules/tasks/services/board.service';
import { Column } from '@modules/tasks/types/column';
import { Project } from '@modules/tasks/types/project';
import { SectionTaskAppearance } from '@modules/tasks/types/section-task-appearance';
import { Subject } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { Labels } from '../../../../i18n/labels';

@Component({
  selector: 'app-project-board-row',
  templateUrl: './project-board-row.component.html',
  styleUrls: ['./project-board-row.component.less'],
  animations: [heightAnimation],
  standalone: false,
})
export class ProjectBoardRowComponent implements OnInit, OnChanges, OnDestroy {
  public sectionForm: FormGroup<SectionFormGroup>;
  public collapsed = false;
  public columns: Column[] = [];

  protected readonly Labels = Labels;

  private alive = new Subject<void>();
  private sectionChanged = new Subject<void>();

  @Input() section: Section;
  @Input() project: Project;
  @Input() appearance: SectionTaskAppearance = 'default';

  public readonly canMoveUp = input(true);
  public readonly canMoveDown = input(true);

  // Callable attributes
  public dndPredicate = (dragData: DragData): boolean => dragData.type === 'task';

  private readonly boardService = inject(BoardService);
  private readonly titleInputRef = viewChild<ElementRef<HTMLInputElement>>('titleInput');

  ngOnInit(): void {
    this.sectionForm = this.section?.asFormGroup();

    // A section just added from "Add new section" puts the cursor in its name, its default title selected.
    if (this.boardService.claimSectionFocus(this.section?.id)) {
      setTimeout(() => {
        const input = this.titleInputRef()?.nativeElement;
        input?.focus();
        input?.select();
      }, 0);
    }

    this.boardService
      .getColumns()
      .pipe(takeUntil(this.alive))
      .subscribe((columns) => {
        this.columns = columns;
      });

    this.sectionChanged
      .pipe(
        filter(() => !!this.section),
        switchMap(() => this.boardService.getSectionCollapsed(this.section.id)),
        takeUntil(this.alive),
      )
      .subscribe((value: boolean) => (this.collapsed = value));

    this.sectionChanged.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('section' in changes) {
      this.sectionChanged.next();
      this.sectionForm = this.section?.asFormGroup();
    }
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  handleBlur() {
    const titleControl = this.sectionForm.get('title');
    if (!titleControl.dirty || titleControl.value.trim() === '') return;

    this.boardService.updateSection(Section.fromFormGroup(this.sectionForm));
  }

  delete(): void {
    this.boardService.removeSection(this.section);
  }

  moveUp(): void {
    this.boardService.moveSection(this.section, -1);
  }

  moveDown(): void {
    this.boardService.moveSection(this.section, 1);
  }

  toggleCollapse(): void {
    this.boardService.setSectionCollapsed(this.section.id, !this.collapsed);
  }

  protected addColumn(): void {
    this.boardService.addColumn();
  }

  protected insertColumnBefore(index: number): void {
    const prevColumn = this.columns[index - 1];
    if (!prevColumn) return;
    this.boardService.addColumn(true, prevColumn.position);
  }
}
